/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import type { ComponentType, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { BriefcaseBusiness, CheckCircle2, Pencil, Plus, Send, Sparkles, Users, X } from "lucide-react";
import { leadSourceOptions, type ClientDTO, type JobDTO, type LeadConversationAnalysisDTO, type LeadDTO, type QuoteDTO, type QuoteItemDTO, type QuotePackageDTO, type ServiceTargetDTO } from "@tendvia/shared";
import { api } from "../api";
import { Button } from "../components/Button";
import { Field } from "../components/Field";

type SalesPanelProps = {
  clients: ClientDTO[];
  targets: ServiceTargetDTO[];
  leads: LeadDTO[];
  quotes: QuoteDTO[];
  packages: QuotePackageDTO[];
  jobs: JobDTO[];
  onRefresh: () => Promise<void>;
};

type AddOnDraft = {
  id: string;
  name: string;
  quantity: string;
  unitPriceDollars: string;
  sortOrder: number;
};

type ActiveSheet = "lead" | "packages" | null;
type QuoteStage = "package" | "details" | "finalize";
type LeadSourceOption = (typeof leadSourceOptions)[number];

const starterAddOn = (): AddOnDraft => ({
  id: `addon-${Math.random().toString(36).slice(2)}`,
  name: "",
  quantity: "1",
  unitPriceDollars: "",
  sortOrder: 1
});

const leadSourceChoices: Array<{ value: LeadSourceOption; label: string }> = [
  { value: "text", label: "Text message" },
  { value: "call", label: "Phone call" },
  { value: "email", label: "Email" },
  { value: "instagram", label: "Instagram DM" },
  { value: "facebook", label: "Facebook DM" },
  { value: "website form", label: "Website form" },
  { value: "repeat customer", label: "Repeat customer" },
  { value: "other", label: "Other" }
];

export function SalesPanel({ clients, targets, leads, quotes, packages, jobs, onRefresh }: SalesPanelProps) {
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);

  const [leadClientId, setLeadClientId] = useState("");
  const [leadTargetId, setLeadTargetId] = useState("");
  const [leadSource, setLeadSource] = useState<LeadSourceOption>("text");
  const [leadSourceDetail, setLeadSourceDetail] = useState("");
  const [leadSummary, setLeadSummary] = useState("");
  const [leadConversationText, setLeadConversationText] = useState("");
  const [leadQuotedDollars, setLeadQuotedDollars] = useState("");
  const [leadScheduledAt, setLeadScheduledAt] = useState("");

  const [packageName, setPackageName] = useState("");
  const [packageDescription, setPackageDescription] = useState("");
  const [packagePriceDollars, setPackagePriceDollars] = useState("");
  const [packageSortOrder, setPackageSortOrder] = useState("0");
  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  const [isPackageFormOpen, setIsPackageFormOpen] = useState(false);

  const [quotePackageId, setQuotePackageId] = useState("");
  const [quoteTitle, setQuoteTitle] = useState("");
  const [quoteNotes, setQuoteNotes] = useState("");
  const [quoteScheduledStart, setQuoteScheduledStart] = useState("");
  const [quoteScheduledEnd, setQuoteScheduledEnd] = useState("");
  const [quoteJobTitle, setQuoteJobTitle] = useState("");
  const [quoteJobNotes, setQuoteJobNotes] = useState("");
  const [addOns, setAddOns] = useState<AddOnDraft[]>([starterAddOn()]);
  const [quoteDirty, setQuoteDirty] = useState(false);
  const [quoteStage, setQuoteStage] = useState<QuoteStage>("package");
  const [analysisByLeadId, setAnalysisByLeadId] = useState<Record<string, LeadConversationAnalysisDTO>>({});
  const [analysisErrorByLeadId, setAnalysisErrorByLeadId] = useState<Record<string, string>>({});
  const [analysisBusyLeadId, setAnalysisBusyLeadId] = useState("");

  useEffect(() => {
    if (!selectedLeadId && leads.length > 0) {
      setSelectedLeadId(leads[0]!.id);
    }
    if (selectedLeadId && !leads.some((lead) => lead.id === selectedLeadId)) {
      setSelectedLeadId(leads[0]?.id ?? "");
    }
  }, [leads, selectedLeadId]);

  const selectedLead = useMemo(() => leads.find((lead) => lead.id === selectedLeadId) ?? null, [leads, selectedLeadId]);
  const selectedQuote = useMemo(() => quotes.find((quote) => quote.leadId === selectedLeadId) ?? null, [quotes, selectedLeadId]);
  const selectedPackage = useMemo(() => packages.find((item) => item.id === quotePackageId) ?? null, [packages, quotePackageId]);
  const primaryPackageId = packages[0]?.id ?? "";
  const clientsById = useMemo(() => new Map(clients.map((client) => [client.id, client])), [clients]);
  const targetsById = useMemo(() => new Map(targets.map((target) => [target.id, target])), [targets]);
  const selectedLeadAnalysis = selectedLeadId ? analysisByLeadId[selectedLeadId] ?? null : null;
  const selectedLeadAnalysisError = selectedLeadId ? analysisErrorByLeadId[selectedLeadId] ?? "" : "";
  const leadTargetOptions = useMemo(
    () => leadClientId ? targets.filter((target) => target.clientId === leadClientId) : targets,
    [leadClientId, targets]
  );
  const sourceNeedsDetail = leadSource === "other";
  const suggestedClientMatch = useMemo(
    () => findBestClientMatch(clients, selectedLeadAnalysis?.clientName ?? null),
    [clients, selectedLeadAnalysis?.clientName]
  );
  const suggestedTargetMatch = useMemo(
    () => findBestTargetMatch(targets, selectedLeadAnalysis?.vehicleLabel ?? null, suggestedClientMatch?.id ?? selectedLead?.clientId ?? null),
    [selectedLead?.clientId, selectedLeadAnalysis?.vehicleLabel, suggestedClientMatch?.id, targets]
  );
  const suggestedPackageMatch = useMemo(
    () => findBestPackageMatch(packages, selectedLeadAnalysis?.packageHint ?? null),
    [packages, selectedLeadAnalysis?.packageHint]
  );
  const suggestedQuoteNotes = useMemo(
    () => buildSuggestedQuoteNotes(selectedLeadAnalysis),
    [selectedLeadAnalysis]
  );

  useEffect(() => {
    if (leadTargetId && !leadTargetOptions.some((target) => target.id === leadTargetId)) {
      setLeadTargetId("");
    }
  }, [leadTargetId, leadTargetOptions]);

  useEffect(() => {
    const lead = leads.find((item) => item.id === selectedLeadId) ?? null;
    if (selectedQuote) {
      setQuotePackageId(selectedQuote.packageId);
      setQuoteTitle(selectedQuote.title);
      setQuoteNotes(selectedQuote.notes ?? "");
      setQuoteScheduledStart(selectedQuote.scheduledStart ? toDatetimeLocal(selectedQuote.scheduledStart) : "");
      setQuoteScheduledEnd(selectedQuote.scheduledEnd ? toDatetimeLocal(selectedQuote.scheduledEnd) : "");
      setQuoteJobTitle(selectedQuote.title);
      setQuoteJobNotes(selectedQuote.notes ?? "");
      const nextAddOns = selectedQuote.items
        .filter((item) => item.itemType === "addon")
        .map((item) => toAddOnDraft(item));
      setAddOns(nextAddOns.length > 0 ? nextAddOns : [starterAddOn()]);
      setQuoteDirty(false);
      return;
    }

    if (!quoteDirty) {
      if (packages.length > 0) {
        setQuotePackageId(packages[0]!.id);
      }
      if (lead) {
        setQuoteTitle(defaultQuoteTitle(packages[0]?.name ?? "Quote", lead.summary));
        setQuoteJobTitle(defaultQuoteTitle(packages[0]?.name ?? "Job", lead.summary));
      }
      if (addOns.length === 0) {
        setAddOns([starterAddOn()]);
      }
    }
  }, [leads, selectedLeadId, selectedQuote?.updatedAt, primaryPackageId, quoteDirty, addOns.length]);

  useEffect(() => {
    setQuoteDirty(false);
  }, [selectedLeadId]);

  useEffect(() => {
    if (!selectedLeadId) {
      setQuoteStage("package");
      return;
    }
    if (!selectedQuote) {
      setQuoteStage("package");
      return;
    }
    if (selectedQuote.status === "sent" || selectedQuote.status === "accepted") {
      setQuoteStage("finalize");
      return;
    }
    setQuoteStage("details");
  }, [selectedLeadId, selectedQuote?.id, selectedQuote?.status]);

  const quoteBasePriceCents = selectedPackage?.basePriceCents ?? 0;
  const liveQuoteItems = buildQuoteItems(selectedPackage, addOns);
  const liveQuoteTotalCents = selectedQuote && !quoteDirty ? selectedQuote.totalCents : quoteTotals(liveQuoteItems);
  const pipelineValueCents = quotes
    .filter((quote) => quote.status === "draft" || quote.status === "sent")
    .reduce((total, quote) => total + quote.totalCents, 0);
  const currentStageGuide = stageGuide(quoteStage);

  function resetLeadForm() {
    setLeadClientId("");
    setLeadTargetId("");
    setLeadSource("text");
    setLeadSourceDetail("");
    setLeadSummary("");
    setLeadConversationText("");
    setLeadQuotedDollars("");
    setLeadScheduledAt("");
  }

  function resetPackageForm() {
    setEditingPackageId(null);
    setPackageName("");
    setPackageDescription("");
    setPackagePriceDollars("");
    setPackageSortOrder("0");
    setIsPackageFormOpen(false);
  }

  function openLeadSheet() {
    setMessage("");
    resetLeadForm();
    setActiveSheet("lead");
  }

  function openPackagesSheet() {
    setMessage("");
    resetPackageForm();
    setActiveSheet("packages");
  }

  function openNewPackageForm() {
    setMessage("");
    setEditingPackageId(null);
    setPackageName("");
    setPackageDescription("");
    setPackagePriceDollars("");
    setPackageSortOrder("0");
    setIsPackageFormOpen(true);
  }

  function closeSheet() {
    setActiveSheet(null);
  }

  async function createLead() {
    if (!leadSummary.trim() || !leadConversationText.trim() || (sourceNeedsDetail && !leadSourceDetail.trim())) return;
    setBusy(true);
    setMessage("");
    try {
      const created = await api.createLead({
        clientId: leadClientId || null,
        targetId: leadTargetId || null,
        source: leadSource,
        sourceDetail: leadSourceDetail.trim() || null,
        summary: leadSummary.trim(),
        conversationText: leadConversationText.trim(),
        quotedCents: leadQuotedDollars.trim() ? dollarsToCents(leadQuotedDollars) : null,
        scheduledAt: leadScheduledAt ? toIsoDateTime(leadScheduledAt) : null
      });
      resetLeadForm();
      setSelectedLeadId(created.id);
      await onRefresh();
      setMessage("Lead added.");
      closeSheet();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to create lead.");
    } finally {
      setBusy(false);
    }
  }

  async function createPackage() {
    if (!packageName.trim() || !packagePriceDollars.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      const isEditing = Boolean(editingPackageId);
      if (editingPackageId) {
        await api.updateQuotePackage(editingPackageId, {
          name: packageName.trim(),
          description: packageDescription.trim() || null,
          basePriceCents: dollarsToCents(packagePriceDollars),
          sortOrder: Number(packageSortOrder) || 0,
          status: "active"
        });
      } else {
        await api.createQuotePackage({
          name: packageName.trim(),
          description: packageDescription.trim() || null,
          basePriceCents: dollarsToCents(packagePriceDollars),
          sortOrder: Number(packageSortOrder) || 0,
          status: "active"
        });
      }
      resetPackageForm();
      await onRefresh();
      setMessage(isEditing ? "Package updated." : "Package added.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save package.");
    } finally {
      setBusy(false);
    }
  }

  function beginPackageEdit(quotePackage: QuotePackageDTO) {
    setMessage("");
    setEditingPackageId(quotePackage.id);
    setPackageName(quotePackage.name);
    setPackageDescription(quotePackage.description ?? "");
    setPackagePriceDollars(String(Math.round(quotePackage.basePriceCents / 100)));
    setPackageSortOrder(String(quotePackage.sortOrder));
    setIsPackageFormOpen(true);
  }

  async function saveDraft() {
    if (!selectedLead || !quotePackageId) return null;
    setBusy(true);
    setMessage("");
    try {
      const quote = await api.createLeadQuoteDraft(selectedLead.id, {
        packageId: quotePackageId,
        title: quoteTitle.trim() || null,
        notes: quoteNotes.trim() || null,
        scheduledStart: quoteScheduledStart ? toIsoDateTime(quoteScheduledStart) : null,
        scheduledEnd: quoteScheduledEnd ? toIsoDateTime(quoteScheduledEnd) : null,
        addOns: addOns
          .filter((item) => item.name.trim())
          .map((item) => ({
            name: item.name.trim(),
            itemType: "addon",
            quantity: Math.max(1, Number(item.quantity) || 1),
            unitPriceCents: dollarsToCents(item.unitPriceDollars),
            sortOrder: item.sortOrder
          }))
      });
      await onRefresh();
      setSelectedLeadId(quote.leadId);
      return quote;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save quote draft.");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function sendQuote() {
    const draft = await saveDraft();
    if (!draft) return;
    setBusy(true);
    setMessage("");
    try {
      await api.sendQuote(draft.id);
      await onRefresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to send quote.");
    } finally {
      setBusy(false);
    }
  }

  async function acceptQuote() {
    const draft = await saveDraft();
    if (!draft) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await api.acceptQuote(draft.id, {
        scheduledStart: quoteScheduledStart ? toIsoDateTime(quoteScheduledStart) : null,
        scheduledEnd: quoteScheduledEnd ? toIsoDateTime(quoteScheduledEnd) : null,
        jobTitle: quoteJobTitle.trim() || null,
        jobNotes: quoteJobNotes.trim() || null
      });
      await onRefresh();
      setMessage(`Quote converted into job ${result.job.title}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to accept quote.");
    } finally {
      setBusy(false);
    }
  }

  async function runConversationAnalysis() {
    if (!selectedLead?.conversationText?.trim()) return;
    setAnalysisBusyLeadId(selectedLead.id);
    setAnalysisErrorByLeadId((current) => ({ ...current, [selectedLead.id]: "" }));
    setMessage("");
    try {
      const analysis = await api.analyzeLeadConversation(selectedLead.id);
      setAnalysisByLeadId((current) => ({ ...current, [selectedLead.id]: analysis }));
    } catch (error) {
      setAnalysisErrorByLeadId((current) => ({
        ...current,
        [selectedLead.id]: error instanceof Error ? error.message : "Unable to analyze that conversation."
      }));
    } finally {
      setAnalysisBusyLeadId("");
    }
  }

  async function applyLeadPatch(input: Parameters<typeof api.updateLead>[1], successMessage: string) {
    if (!selectedLead) return;
    setBusy(true);
    setMessage("");
    try {
      await api.updateLead(selectedLead.id, input);
      await onRefresh();
      setMessage(successMessage);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update the lead.");
    } finally {
      setBusy(false);
    }
  }

  function applyPackageSuggestion() {
    if (!suggestedPackageMatch) return;
    setQuoteDirty(true);
    setQuotePackageId(suggestedPackageMatch.id);
    setQuoteStage("package");
    setMessage(`Package suggestion loaded: ${suggestedPackageMatch.name}.`);
  }

  function applySuggestedQuoteNotes() {
    if (!suggestedQuoteNotes) return;
    setQuoteDirty(true);
    setQuoteNotes((current) => mergeMultilineText(current, suggestedQuoteNotes));
    setQuoteStage("details");
    setMessage("Conversation notes loaded into the quote workspace.");
  }

  useEffect(() => {
    if (!activeSheet) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeSheet();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeSheet]);

  return (
    <div className="grid gap-5">
      <SectionHeader
        icon={Sparkles}
        title="Sales"
        description="Capture the request, choose the package, then turn a yes into booked work."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric icon={Users} label="Leads" value={leads.length} />
        <Metric icon={BriefcaseBusiness} label="Quotes" value={quotes.length} />
        <Metric icon={Send} label="Pipeline" value={formatMoney(pipelineValueCents)} />
        <Metric icon={CheckCircle2} label="Jobs" value={jobs.length} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button icon={<Plus className="size-4" aria-hidden="true" />} type="button" onClick={openLeadSheet}>
          From conversation
        </Button>
        <Button variant="secondary" icon={<Sparkles className="size-4" aria-hidden="true" />} type="button" onClick={openPackagesSheet}>
          Packages
        </Button>
      </div>

      {message && activeSheet === null ? <p className="rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-medium text-muted">{message}</p> : null}

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <section className="grid gap-6">
          <PanelSection title="Recent leads">
            <div className="grid gap-2">
              {leads.length === 0 ? (
                <EmptyState label="No leads yet" detail="Start with the customer request. A lead can come from a call, text, DM, or repeat customer." />
              ) : leads.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  className={`rounded-token border p-3 text-left transition ${
                    selectedLeadId === lead.id ? "border-primary bg-surface-soft" : "border-border/50 bg-surface hover:bg-surface-soft"
                  }`}
                  onClick={() => setSelectedLeadId(lead.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-extrabold">{lead.summary}</p>
                      <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">
                        {leadContextLabel(lead, clientsById, targetsById)}
                      </p>
                      <p className="mt-1 text-sm text-muted">{lead.status} · {lead.quotedCents ? formatMoney(lead.quotedCents) : "No quote"}</p>
                    </div>
                    <span className="rounded-pill bg-surface-soft px-2.5 py-1 text-xs font-extrabold text-primary">{formatLeadSource(lead.source)}</span>
                  </div>
                </button>
              ))}
            </div>
          </PanelSection>

          {selectedLead ? (
            <PanelSection title="Quote workspace">
              <div className="grid gap-4">
                <div className="rounded-token border border-border/45 bg-surface px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-primary">Active lead</p>
                      <p className="mt-2 text-lg font-extrabold text-text">{selectedLead.summary}</p>
                      <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">
                        {leadContextLabel(selectedLead, clientsById, targetsById)}
                      </p>
                      <p className="mt-1 text-sm text-muted">
                        {selectedLead.status} · {selectedLead.quotedCents ? formatMoney(selectedLead.quotedCents) : "No quote yet"}
                      </p>
                    </div>
                    <Button variant="secondary" type="button" onClick={openLeadSheet}>
                      New intake
                    </Button>
                  </div>

                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    <DataRow label="Source" value={leadSourceSummary(selectedLead)} />
                    <DataRow label="Conversation" value={selectedLead.conversationText ? "Captured" : "Not saved"} />
                  </div>

                  {selectedLead.conversationText ? (
                    <div className="mt-3 rounded-token border border-border/45 bg-surface-soft px-4 py-3">
                      <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">Conversation context</p>
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-text">{selectedLead.conversationText}</p>
                    </div>
                  ) : null}

                  {selectedLead.conversationText ? (
                    <div className="mt-3 grid gap-3 rounded-token border border-border/45 bg-surface-soft px-4 py-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">Conversation assist</p>
                          <p className="mt-1 text-sm leading-6 text-muted">Analyze the pasted conversation, then choose exactly which suggestions to apply.</p>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {selectedLeadAnalysis && suggestedQuoteNotes ? (
                            <Button variant="secondary" type="button" onClick={applySuggestedQuoteNotes}>
                              Use notes in quote
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            disabled={analysisBusyLeadId === selectedLead.id}
                            onClick={() => { void runConversationAnalysis(); }}
                          >
                            {analysisBusyLeadId === selectedLead.id ? "Analyzing" : selectedLeadAnalysis ? "Re-run analysis" : "Analyze conversation"}
                          </Button>
                        </div>
                      </div>

                      {selectedLeadAnalysisError ? (
                        <p className="rounded-token border border-border/60 bg-surface px-3 py-2 text-sm font-medium text-muted">{selectedLeadAnalysisError}</p>
                      ) : null}

                      {selectedLeadAnalysis ? (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <SuggestionCard
                            label="Lead summary"
                            value={selectedLeadAnalysis.summary ?? "No suggestion"}
                            detail={selectedLead.summary === (selectedLeadAnalysis.summary ?? "") ? "Already applied to this lead." : undefined}
                            actionLabel={selectedLeadAnalysis.summary && selectedLead.summary !== selectedLeadAnalysis.summary ? "Use summary" : undefined}
                            onAction={selectedLeadAnalysis.summary
                              ? () => { void applyLeadPatch({ summary: selectedLeadAnalysis.summary ?? undefined }, "Lead summary updated from analysis."); }
                              : undefined}
                            disabled={busy}
                          />
                          <SuggestionCard
                            label="Timing"
                            value={formatTimingSuggestion(selectedLeadAnalysis)}
                            detail={selectedLeadAnalysis.scheduledAt
                              ? (selectedLead.scheduledAt === selectedLeadAnalysis.scheduledAt ? "Already attached to this lead." : "Exact time was detected from the conversation.")
                              : "No exact appointment timestamp was detected, so this stays as a note."
                            }
                            actionLabel={selectedLeadAnalysis.scheduledAt && selectedLead.scheduledAt !== selectedLeadAnalysis.scheduledAt ? "Use timing" : undefined}
                            onAction={selectedLeadAnalysis.scheduledAt
                              ? () => { void applyLeadPatch({ scheduledAt: selectedLeadAnalysis.scheduledAt ?? undefined }, "Lead timing updated from analysis."); }
                              : undefined}
                            disabled={busy}
                          />
                          <SuggestionCard
                            label="Client"
                            value={selectedLeadAnalysis.clientName ?? "No client name detected"}
                            detail={selectedLeadAnalysis.clientPhone || selectedLeadAnalysis.clientEmail
                              ? [selectedLeadAnalysis.clientPhone, selectedLeadAnalysis.clientEmail].filter(Boolean).join(" · ")
                              : clientSuggestionDetail(selectedLead, suggestedClientMatch)
                            }
                            actionLabel={suggestedClientMatch && selectedLead.clientId !== suggestedClientMatch.id ? "Attach client" : undefined}
                            onAction={suggestedClientMatch
                              ? () => { void applyLeadPatch({ clientId: suggestedClientMatch.id }, `Attached ${suggestedClientMatch.name} to this lead.`); }
                              : undefined}
                            disabled={busy}
                          />
                          <SuggestionCard
                            label="Vehicle"
                            value={selectedLeadAnalysis.vehicleLabel ?? "No vehicle detected"}
                            detail={selectedLeadAnalysis.serviceAddress ?? targetSuggestionDetail(selectedLead, suggestedTargetMatch)}
                            actionLabel={suggestedTargetMatch && selectedLead.targetId !== suggestedTargetMatch.id ? "Attach vehicle" : undefined}
                            onAction={suggestedTargetMatch
                              ? () => {
                                void applyLeadPatch(
                                  { clientId: suggestedTargetMatch.clientId, targetId: suggestedTargetMatch.id },
                                  `Attached ${suggestedTargetMatch.label} to this lead.`
                                );
                              }
                              : undefined}
                            disabled={busy}
                          />
                          <SuggestionCard
                            label="Package hint"
                            value={selectedLeadAnalysis.packageHint ?? "No package hint"}
                            detail={selectedLeadAnalysis.requestedServices.length > 0 ? selectedLeadAnalysis.requestedServices.join(" · ") : "No service list suggested."}
                            actionLabel={suggestedPackageMatch && quotePackageId !== suggestedPackageMatch.id ? "Load package" : undefined}
                            onAction={suggestedPackageMatch ? applyPackageSuggestion : undefined}
                            disabled={busy}
                          />
                          <SuggestionCard
                            label="Operator notes"
                            value={selectedLeadAnalysis.notes.length > 0 ? selectedLeadAnalysis.notes.join(" · ") : "No extra notes"}
                            detail={`Confidence: ${selectedLeadAnalysis.confidence}`}
                            actionLabel={suggestedQuoteNotes ? "Use in quote" : undefined}
                            onAction={suggestedQuoteNotes ? applySuggestedQuoteNotes : undefined}
                            disabled={busy}
                          />
                        </div>
                      ) : (
                        <p className="text-sm leading-6 text-muted">No suggestions yet. Run analysis when you want help turning the raw conversation into structured next steps.</p>
                      )}
                    </div>
                  ) : null}

                  <div className="mt-4 grid gap-2 sm:grid-cols-4">
                    {workflowSteps(selectedQuote).map((step) => (
                      <div
                        key={step.label}
                        className={`rounded-token border px-3 py-2 ${step.current ? "border-primary bg-surface-soft" : "border-border/40 bg-surface"}`}
                      >
                        <p className={`text-[11px] font-extrabold uppercase tracking-[0.16em] ${step.complete || step.current ? "text-primary" : "text-muted"}`}>
                          {step.label}
                        </p>
                        <p className="mt-1 text-sm font-bold text-text">{step.copy}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <StageButton label="1. Package" active={quoteStage === "package"} onClick={() => setQuoteStage("package")} />
                  <StageButton label="2. Details" active={quoteStage === "details"} onClick={() => setQuoteStage("details")} />
                  <StageButton label="3. Finalize" active={quoteStage === "finalize"} onClick={() => setQuoteStage("finalize")} />
                </div>

                <div className="rounded-token border border-border/45 bg-surface-soft px-4 py-3">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">{currentStageGuide.label}</p>
                  <p className="mt-1 text-sm font-extrabold text-text">{currentStageGuide.title}</p>
                  <p className="mt-1 text-sm leading-6 text-muted">{currentStageGuide.detail}</p>
                </div>

                {quoteStage === "package" ? (
                  <div className="grid gap-4">
                    {packages.length === 0 ? (
                      <EmptyState label="No packages ready yet" actionLabel="Open packages" onAction={openPackagesSheet} />
                    ) : (
                      <div className="grid gap-2">
                        {packages.map((quotePackage) => (
                          <button
                            key={quotePackage.id}
                            type="button"
                            className={`rounded-token border px-4 py-3 text-left transition ${
                              quotePackageId === quotePackage.id ? "border-primary bg-surface-soft" : "border-border/45 bg-surface hover:bg-surface-soft"
                            }`}
                            onClick={() => {
                              setQuoteDirty(true);
                              setQuotePackageId(quotePackage.id);
                            }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-extrabold text-text">{quotePackage.name}</p>
                                <p className="mt-1 text-sm text-muted">{quotePackage.description ?? "No description"}</p>
                              </div>
                              <span className="text-sm font-extrabold text-primary">{formatMoney(quotePackage.basePriceCents)}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="grid gap-3">
                      <Field label="Quote title" value={quoteTitle} onChange={(event) => {
                        setQuoteDirty(true);
                        setQuoteTitle(event.target.value);
                      }} />
                      <Field label="Quote notes" value={quoteNotes} onChange={(event) => {
                        setQuoteDirty(true);
                        setQuoteNotes(event.target.value);
                      }} />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="secondary" type="button" onClick={openPackagesSheet}>
                        Manage packages
                      </Button>
                      <Button type="button" disabled={!quotePackageId} onClick={() => setQuoteStage("details")}>
                        Next: details
                      </Button>
                    </div>
                  </div>
                ) : null}

                {quoteStage === "details" ? (
                  <div className="grid gap-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Start time" type="datetime-local" value={quoteScheduledStart} onChange={(event) => {
                        setQuoteDirty(true);
                        setQuoteScheduledStart(event.target.value);
                      }} />
                      <Field label="End time" type="datetime-local" value={quoteScheduledEnd} onChange={(event) => {
                        setQuoteDirty(true);
                        setQuoteScheduledEnd(event.target.value);
                      }} />
                    </div>

                    <div className="grid gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-extrabold uppercase tracking-wide text-muted">Add-ons</h3>
                        <Button variant="secondary" icon={<Plus className="size-4" aria-hidden="true" />} type="button" onClick={() => {
                          setQuoteDirty(true);
                          setAddOns((current) => [...current, starterAddOn()]);
                        }}>
                          Add row
                        </Button>
                      </div>
                      <div className="overflow-hidden rounded-token border border-border/40 bg-surface-soft">
                        {addOns.map((row, index) => (
                          <div key={row.id} className={`grid gap-3 p-3 ${index > 0 ? "border-t border-border/40" : ""}`}>
                            <div className="grid gap-2 sm:grid-cols-2">
                              <Field label={`Item ${index + 1}`} value={row.name} onChange={(event) => {
                                setQuoteDirty(true);
                                setAddOns((current) => current.map((item) => (item.id === row.id ? { ...item, name: event.target.value } : item)));
                              }} />
                              <Field label="Quantity" type="number" min="1" step="1" value={row.quantity} onChange={(event) => {
                                setQuoteDirty(true);
                                setAddOns((current) => current.map((item) => (item.id === row.id ? { ...item, quantity: event.target.value } : item)));
                              }} />
                              <Field label="Unit price" type="number" min="0" step="1" value={row.unitPriceDollars} onChange={(event) => {
                                setQuoteDirty(true);
                                setAddOns((current) => current.map((item) => (item.id === row.id ? { ...item, unitPriceDollars: event.target.value } : item)));
                              }} />
                              <Field label="Sort order" type="number" min="0" step="1" value={String(row.sortOrder)} onChange={(event) => {
                                setQuoteDirty(true);
                                setAddOns((current) => current.map((item) => (item.id === row.id ? { ...item, sortOrder: Number(event.target.value) || 0 } : item)));
                              }} />
                            </div>
                            <div className="flex justify-end">
                              <Button variant="ghost" type="button" onClick={() => {
                                setQuoteDirty(true);
                                setAddOns((current) => current.filter((item) => item.id !== row.id));
                              }} disabled={addOns.length === 1}>
                                Remove row
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="secondary" type="button" onClick={() => setQuoteStage("package")}>
                        Back
                      </Button>
                      <Button type="button" disabled={!quotePackageId} onClick={() => setQuoteStage("finalize")}>
                        Next: finalize
                      </Button>
                    </div>
                  </div>
                ) : null}

                {quoteStage === "finalize" ? (
                  <div className="grid gap-4">
                    <div className="rounded-token border border-border/45 bg-surface p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-extrabold">Draft total</p>
                          <p className="mt-1 text-xs font-medium text-muted">Package base: {formatMoney(quoteBasePriceCents)}</p>
                        </div>
                        <p className="text-2xl font-extrabold text-primary">{formatMoney(liveQuoteTotalCents)}</p>
                      </div>
                      <div className="mt-3 grid gap-2">
                        {liveQuoteItems.map((item) => (
                          <div key={`${item.itemType}-${item.sortOrder}-${item.name}`} className="flex items-center justify-between gap-3 rounded-token border border-border/35 bg-surface-soft px-3 py-2">
                            <span className="font-bold">{item.name}</span>
                            <span className="text-sm text-muted">{item.quantity} x {formatMoney(item.unitPriceCents)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <Field label="Job title" value={quoteJobTitle} onChange={(event) => {
                        setQuoteDirty(true);
                        setQuoteJobTitle(event.target.value);
                      }} />
                      <Field label="Job notes" value={quoteJobNotes} onChange={(event) => {
                        setQuoteDirty(true);
                        setQuoteJobNotes(event.target.value);
                      }} />
                    </div>

                    <div className="grid gap-2 sm:grid-cols-3">
                      <Button variant="secondary" icon={<CheckCircle2 className="size-4" aria-hidden="true" />} type="button" disabled={busy || !selectedLead || !quotePackageId} onClick={saveDraft}>
                        Save draft
                      </Button>
                      <Button variant="secondary" icon={<Send className="size-4" aria-hidden="true" />} type="button" disabled={busy || !selectedLead || !quotePackageId} onClick={sendQuote}>
                        Send quote
                      </Button>
                      <Button icon={<CheckCircle2 className="size-4" aria-hidden="true" />} type="button" disabled={busy || !selectedLead || !quotePackageId} onClick={acceptQuote}>
                        Accept quote
                      </Button>
                    </div>

                    <Button variant="ghost" type="button" className="justify-self-start" onClick={() => setQuoteStage("details")}>
                      Back to details
                    </Button>
                  </div>
                ) : null}
              </div>
            </PanelSection>
          ) : (
            <PanelSection title="How this screen works">
              <div className="grid gap-2">
                <GuideNoteRow
                  label="1. Capture the request"
                  detail="A lead is the customer request, not the booked job."
                />
                <GuideNoteRow
                  label="2. Price the work"
                  detail="Choose a package, add timing and add-ons, then save or send the quote."
                />
                <GuideNoteRow
                  label="3. Book the job"
                  detail="When the customer confirms, accept the quote and TendVia creates the job."
                />
              </div>
            </PanelSection>
          )}
        </section>

        <aside className="grid gap-6">
          {selectedLead ? (
            <PanelSection title="Quote snapshot">
              <div className="grid gap-3">
                <div className="rounded-token border border-border/45 bg-surface px-4 py-3">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">Next step</p>
                  <p className="mt-2 text-base font-extrabold text-text">{nextActionCopy(selectedQuote)}</p>
                  <p className="mt-1 text-sm text-muted">{nextActionSupport(selectedQuote)}</p>
                </div>

                {selectedQuote ? (
                  <>
                    <div className="grid gap-2">
                      <DataRow label="Status" value={selectedQuote.status} />
                      <DataRow label="Total" value={formatMoney(selectedQuote.totalCents)} />
                      <DataRow label="Accepted" value={selectedQuote.acceptedAt ? new Date(selectedQuote.acceptedAt).toLocaleString() : "Not yet"} />
                      <DataRow label="Job" value={selectedQuote.convertedJobId ?? "Not converted"} />
                    </div>
                    <div className="overflow-hidden rounded-token border border-border/40 bg-surface-soft">
                      {selectedQuote.items.map((item, index) => (
                        <div key={item.id} className={`flex items-center justify-between gap-3 px-3 py-2 ${index > 0 ? "border-t border-border/40" : ""}`}>
                          <span className="font-bold">{item.name}</span>
                          <span className="text-sm text-muted">{item.quantity} x {formatMoney(item.unitPriceCents)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <EmptyState label="No saved quote yet" />
                )}
              </div>
            </PanelSection>
          ) : null}
        </aside>
      </div>

      {activeSheet === "lead" ? (
        <BottomSheet
          title="New lead from conversation"
          description="Paste the customer request, save the summary, and keep the raw conversation attached to the lead."
          notice={message}
          onClose={closeSheet}
        >
          <form
            id="lead-form"
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void createLead();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField label="Source" value={leadSource} onChange={(event) => setLeadSource(event.target.value as LeadSourceOption)}>
                {leadSourceChoices.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </SelectField>
              {sourceNeedsDetail ? (
                <Field
                  label="Source detail"
                  value={leadSourceDetail}
                  onChange={(event) => setLeadSourceDetail(event.target.value)}
                  placeholder="Nextdoor referral, missed call, walk-up, etc."
                />
              ) : (
                <div className="hidden sm:block" aria-hidden="true" />
              )}
              <div className="sm:col-span-2">
                <Field label="Lead summary" value={leadSummary} onChange={(event) => setLeadSummary(event.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <TextareaField
                  label="Conversation text or notes"
                  value={leadConversationText}
                  onChange={(event) => setLeadConversationText(event.target.value)}
                  rows={7}
                  placeholder="Paste the text thread, DM, email, or your call notes here."
                  hint="Keep the raw request here so the quote workspace still has the customer context."
                />
              </div>
              <SelectField label="Client" value={leadClientId} onChange={(event) => setLeadClientId(event.target.value)}>
                <option value="">No client yet</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </SelectField>
              <SelectField label="Vehicle" value={leadTargetId} onChange={(event) => setLeadTargetId(event.target.value)}>
                <option value="">No vehicle yet</option>
                {leadTargetOptions.map((target) => (
                  <option key={target.id} value={target.id}>{target.label}</option>
                ))}
              </SelectField>
              <Field label="Quoted dollars" type="number" min="0" step="1" value={leadQuotedDollars} onChange={(event) => setLeadQuotedDollars(event.target.value)} />
              <Field label="Scheduled at" type="datetime-local" value={leadScheduledAt} onChange={(event) => setLeadScheduledAt(event.target.value)} />
            </div>
          </form>
          <SheetFooter>
            <Button variant="secondary" type="button" onClick={closeSheet}>Cancel</Button>
            <Button
              icon={<Plus className="size-4" aria-hidden="true" />}
              type="submit"
              form="lead-form"
              disabled={busy || !leadSummary.trim() || !leadConversationText.trim() || (sourceNeedsDetail && !leadSourceDetail.trim())}
            >
              Save lead
            </Button>
          </SheetFooter>
        </BottomSheet>
      ) : null}

      {activeSheet === "packages" ? (
        <BottomSheet title="Packages" notice={message} onClose={closeSheet}>
          <div className="grid gap-4">
            {isPackageFormOpen ? (
              <form
                id="package-form"
                className="grid gap-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  void createPackage();
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-extrabold text-text">{editingPackageId ? "Edit package" : "New package"}</p>
                  <Button variant="ghost" type="button" onClick={resetPackageForm}>
                    Back to list
                  </Button>
                </div>
                <Field label="Package name" value={packageName} onChange={(event) => setPackageName(event.target.value)} />
                <Field label="Description" value={packageDescription} onChange={(event) => setPackageDescription(event.target.value)} />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Base price" type="number" min="0" step="1" value={packagePriceDollars} onChange={(event) => setPackagePriceDollars(event.target.value)} />
                  <Field label="Sort order" type="number" min="0" step="1" value={packageSortOrder} onChange={(event) => setPackageSortOrder(event.target.value)} />
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-extrabold text-text">{packages.length} package{packages.length === 1 ? "" : "s"} ready</p>
                  <Button icon={<Plus className="size-4" aria-hidden="true" />} type="button" onClick={openNewPackageForm}>
                    New package
                  </Button>
                </div>

                <div className="overflow-hidden rounded-token border border-border/40 bg-surface-soft">
                  {packages.map((quotePackage, index) => (
                    <div key={quotePackage.id} className={`flex items-start justify-between gap-3 p-3 ${index > 0 ? "border-t border-border/40" : ""}`}>
                      <div>
                        <p className="font-extrabold">{quotePackage.name}</p>
                        <p className="mt-1 text-sm text-muted">{quotePackage.description ?? "No description"}</p>
                      </div>
                      <div className="grid justify-items-end gap-2">
                        <span className="rounded-pill bg-surface px-2.5 py-1 text-xs font-extrabold text-primary">{formatMoney(quotePackage.basePriceCents)}</span>
                        <Button
                          variant="ghost"
                          icon={<Pencil className="size-4" aria-hidden="true" />}
                          type="button"
                          className="min-h-9 px-2.5 py-1.5 text-xs"
                          onClick={() => beginPackageEdit(quotePackage)}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <SheetFooter>
            <Button variant="secondary" type="button" onClick={closeSheet}>Close</Button>
            {isPackageFormOpen ? (
              <Button icon={<Sparkles className="size-4" aria-hidden="true" />} type="submit" form="package-form" disabled={busy || !packageName.trim() || !packagePriceDollars.trim()}>
                {editingPackageId ? "Update package" : "Save package"}
              </Button>
            ) : (
              <div />
            )}
          </SheetFooter>
        </BottomSheet>
      ) : null}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  description
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <IconMedallion icon={Icon} />
      <div>
        <h1 className="text-[2rem] font-black tracking-[-0.04em] text-text sm:text-[2.15rem]">{title}</h1>
        {description ? <p className="mt-1 max-w-2xl text-sm leading-6 text-muted">{description}</p> : null}
      </div>
    </div>
  );
}

function PanelSection({ title, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="grid gap-4 border-t border-border/45 pt-4 first:border-t-0 first:pt-0">
      <div>
        <h2 className="text-base font-extrabold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Metric({ icon: Icon, label, value }: { icon: ComponentType<{ className?: string }>; label: string; value: number | string }) {
  return (
    <div className="rounded-token border border-border/50 bg-surface p-3">
      <div className="flex items-center gap-2">
        <Icon className="size-4 shrink-0 text-primary" />
        <p className="text-xl font-extrabold">{value}</p>
      </div>
      <p className="mt-1 text-xs font-bold text-muted">{label}</p>
    </div>
  );
}

function StageButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`min-h-11 rounded-token border px-3 py-2 text-sm font-extrabold transition ${
        active ? "border-primary bg-surface-soft text-primary" : "border-border/45 bg-surface text-muted hover:bg-surface-soft hover:text-text"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function BottomSheet({
  title,
  description,
  notice,
  onClose,
  children
}: {
  title: string;
  description?: string;
  notice?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-slate-950/40 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-sm sm:items-center sm:pb-3">
      <button aria-label="Close sheet" className="absolute inset-0 cursor-default" type="button" onClick={onClose} />
      <div
        className="relative z-10 flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[2rem] border border-border/60 bg-surface shadow-float sm:rounded-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${title.toLowerCase().replaceAll(" ", "-")}-title`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border/50 bg-surface px-4 pb-4 pt-4 sm:px-5">
          <div className="flex items-start gap-3">
            <IconMedallion icon={title.toLowerCase().includes("lead") ? Users : Sparkles} />
            <div>
              <h2 id={`${title.toLowerCase().replaceAll(" ", "-")}-title`} className="text-lg font-extrabold">
                {title}
              </h2>
              {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
            </div>
          </div>
          <button
            className="inline-grid size-10 shrink-0 place-items-center rounded-token border border-border/70 bg-elevated text-text transition hover:bg-surface-soft"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-4 overflow-y-auto px-4 py-4 sm:px-5">
          {notice ? <p className="rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-medium text-muted">{notice}</p> : null}
          {children}
        </div>
      </div>
    </div>
  );
}

function SheetFooter({ children }: { children: ReactNode }) {
  return (
    <div className="border-t border-border/50 bg-surface px-4 py-4 sm:px-5">
      <div className="grid gap-2 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function EmptyState({
  label,
  detail,
  actionLabel,
  onAction
}: {
  label: string;
  detail?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="grid gap-4 rounded-token border border-dashed border-border bg-surface-soft px-4 py-5 text-sm font-bold text-muted">
      <p>{label}</p>
      {detail ? <p className="-mt-2 text-sm font-medium leading-6 text-muted">{detail}</p> : null}
      {actionLabel && onAction ? (
        <Button type="button" onClick={onAction} className="justify-self-start">
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-token border border-border/35 bg-surface-soft px-3 py-2 text-sm">
      <span className="font-bold text-text">{label}</span>
      <span className="font-medium text-muted">{value}</span>
    </div>
  );
}

function GuideNoteRow({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="rounded-token border border-border/35 bg-surface px-3 py-3">
      <p className="font-extrabold text-text">{label}</p>
      <p className="mt-1 text-sm leading-6 text-muted">{detail}</p>
    </div>
  );
}

function SuggestionCard({
  label,
  value,
  detail,
  actionLabel,
  onAction,
  disabled
}: {
  label: string;
  value: string;
  detail?: string;
  actionLabel?: string;
  onAction?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-3 rounded-token border border-border/35 bg-surface px-3 py-3">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-primary">{label}</p>
        <p className="mt-2 text-sm font-extrabold leading-6 text-text">{value}</p>
        {detail ? <p className="mt-1 text-sm leading-6 text-muted">{detail}</p> : null}
      </div>
      {actionLabel && onAction ? (
        <Button variant="secondary" type="button" disabled={disabled} onClick={onAction} className="justify-self-start">
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function SelectField({ label, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-medium text-text">
      <span>{label}</span>
      <select
        className="min-h-12 rounded-token border border-border/70 bg-elevated px-3 py-2 text-base text-text"
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

function TextareaField({
  label,
  hint,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string }) {
  return (
    <label className="grid gap-2 text-sm text-text">
      <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-muted">{label}</span>
      <textarea
        className="min-h-32 rounded-token border border-border/70 bg-elevated px-3 py-2.5 text-base font-medium text-text transition placeholder:text-muted/80 focus:border-primary focus:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/20"
        {...props}
      />
      {hint ? <span className="text-xs font-medium leading-5 text-muted">{hint}</span> : null}
    </label>
  );
}

function IconMedallion({ icon: Icon }: { icon: ComponentType<{ className?: string }> }) {
  return (
    <span className="inline-grid size-10 shrink-0 place-items-center rounded-full border border-border/50 bg-surface-soft text-primary">
      <Icon className="size-5" />
    </span>
  );
}

function workflowSteps(selectedQuote: QuoteDTO | null) {
  const status = selectedQuote?.status ?? "new";
  return [
    {
      label: "Lead",
      copy: "Captured",
      complete: true,
      current: status === "new"
    },
    {
      label: "Draft",
      copy: "Build quote",
      complete: Boolean(selectedQuote),
      current: status === "draft"
    },
    {
      label: "Sent",
      copy: "Waiting",
      complete: status === "sent" || status === "accepted",
      current: status === "sent"
    },
    {
      label: "Booked",
      copy: "Job ready",
      complete: status === "accepted",
      current: status === "accepted"
    }
  ];
}

function nextActionCopy(selectedQuote: QuoteDTO | null) {
  if (!selectedQuote) return "Choose the package you would actually send this customer.";
  if (selectedQuote.status === "draft") return "Finish the pricing and timing, then send or book it.";
  if (selectedQuote.status === "sent") return "The quote is out. Book the job the moment the customer confirms.";
  if (selectedQuote.status === "accepted") return "This work is booked. The next stop is Jobs.";
  return "Keep moving this lead toward a booked job.";
}

function nextActionSupport(selectedQuote: QuoteDTO | null) {
  if (!selectedQuote) return "Package, Details, then Finalize is the full path from customer request to booked job.";
  if (selectedQuote.status === "draft") return "Move through the three stages without losing the draft. Save it when you need to pause.";
  if (selectedQuote.status === "sent") return "Once you accept the quote, TendVia creates the appointment and job automatically.";
  if (selectedQuote.status === "accepted") return "Open Jobs to run the work and keep the field day moving.";
  return "This panel tracks what should happen next for the active lead.";
}

function leadContextLabel(
  lead: LeadDTO,
  clientsById: Map<string, ClientDTO>,
  targetsById: Map<string, ServiceTargetDTO>
) {
  const client = lead.clientId ? clientsById.get(lead.clientId) ?? null : null;
  const target = lead.targetId ? targetsById.get(lead.targetId) ?? null : null;
  if (client && target) return `${client.name} · ${target.label}`;
  if (client) return client.name;
  if (target) return target.label;
  return "Customer details pending";
}

function formatLeadSource(source: string) {
  const labels: Record<string, string> = {
    text: "Text message",
    call: "Phone call",
    email: "Email",
    instagram: "Instagram DM",
    facebook: "Facebook DM",
    "website form": "Website form",
    "repeat customer": "Repeat customer",
    other: "Other",
    manual: "Manual"
  };
  return labels[source] ?? source;
}

function leadSourceSummary(lead: LeadDTO) {
  const label = formatLeadSource(lead.source);
  return lead.sourceDetail ? `${label} · ${lead.sourceDetail}` : label;
}

function clientSuggestionDetail(lead: LeadDTO, matchedClient: ClientDTO | null) {
  if (matchedClient) {
    return lead.clientId === matchedClient.id ? "Already attached to this lead." : `Matches existing client: ${matchedClient.name}`;
  }
  return "No existing client match found. Create or update it in Clients if needed.";
}

function targetSuggestionDetail(lead: LeadDTO, matchedTarget: ServiceTargetDTO | null) {
  if (matchedTarget) {
    return lead.targetId === matchedTarget.id ? "Already attached to this lead." : `Matches existing vehicle: ${matchedTarget.label}`;
  }
  return "No existing vehicle match found yet.";
}

function formatTimingSuggestion(analysis: LeadConversationAnalysisDTO) {
  if (analysis.scheduledAt) {
    return new Date(analysis.scheduledAt).toLocaleString();
  }
  return analysis.scheduleNote ?? "No timing suggestion";
}

function stageGuide(stage: QuoteStage) {
  if (stage === "package") {
    return {
      label: "Stage 1",
      title: "Choose the offer you want to put in front of the customer.",
      detail: "Start with the package, then adjust the title and notes so the quote reads like the job you are actually selling."
    };
  }

  if (stage === "details") {
    return {
      label: "Stage 2",
      title: "Add timing and extras before you send anything.",
      detail: "Use this step for the service window, add-ons, and anything that changes the real field workload."
    };
  }

  return {
    label: "Stage 3",
    title: "Review the numbers, then save, send, or book the work.",
    detail: "Finalize is where the quote turns into a real commitment. Accepting it creates the job automatically."
  };
}

function toAddOnDraft(item: QuoteItemDTO): AddOnDraft {
  return {
    id: item.id,
    name: item.name,
    quantity: String(item.quantity),
    unitPriceDollars: String(Math.round(item.unitPriceCents / 100)),
    sortOrder: item.sortOrder
  };
}

function buildQuoteItems(quotePackage: QuotePackageDTO | null, addOns: AddOnDraft[]) {
  const packageItem = quotePackage
    ? [{ id: quotePackage.id, itemType: "package", name: quotePackage.name, quantity: 1, unitPriceCents: quotePackage.basePriceCents, sortOrder: quotePackage.sortOrder }]
    : [];
  const addOnItems = addOns
    .filter((item) => item.name.trim())
    .map((item) => ({
      id: item.id,
      itemType: "addon",
      name: item.name.trim(),
      quantity: Math.max(1, Number(item.quantity) || 1),
      unitPriceCents: dollarsToCents(item.unitPriceDollars),
      sortOrder: item.sortOrder
    }));
  return [...packageItem, ...addOnItems];
}

function quoteTotals(items: Array<{ quantity: number; unitPriceCents: number }>) {
  return items.reduce((total, item) => total + item.quantity * item.unitPriceCents, 0);
}

function dollarsToCents(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : 0;
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(cents / 100);
}

function toDatetimeLocal(value: string) {
  const date = new Date(value);
  const pad = (input: number) => String(input).padStart(2, "0");
  return [
    date.getFullYear(),
    "-",
    pad(date.getMonth() + 1),
    "-",
    pad(date.getDate()),
    "T",
    pad(date.getHours()),
    ":",
    pad(date.getMinutes())
  ].join("");
}

function toIsoDateTime(value: string) {
  return value ? new Date(value).toISOString() : null;
}

function defaultQuoteTitle(packageName: string, summary: string) {
  const trimmed = summary.trim();
  return trimmed ? `${packageName} - ${trimmed.slice(0, 48)}` : packageName;
}

function findBestClientMatch(clients: ClientDTO[], candidate: string | null) {
  if (!candidate) return null;
  const normalizedCandidate = normalizeForMatch(candidate);
  return clients.find((client) => normalizeForMatch(client.name) === normalizedCandidate)
    ?? clients.find((client) => normalizeForMatch(client.name).includes(normalizedCandidate) || normalizedCandidate.includes(normalizeForMatch(client.name)))
    ?? null;
}

function findBestTargetMatch(targets: ServiceTargetDTO[], candidate: string | null, clientId: string | null) {
  if (!candidate) return null;
  const normalizedCandidate = normalizeForMatch(candidate);
  const scopedTargets = clientId ? targets.filter((target) => target.clientId === clientId) : targets;
  return scopedTargets.find((target) => normalizeForMatch(target.label) === normalizedCandidate)
    ?? scopedTargets.find((target) => normalizeForMatch(target.label).includes(normalizedCandidate) || normalizedCandidate.includes(normalizeForMatch(target.label)))
    ?? null;
}

function findBestPackageMatch(packages: QuotePackageDTO[], candidate: string | null) {
  if (!candidate) return null;
  const normalizedCandidate = normalizeForMatch(candidate);
  return packages.find((item) => normalizeForMatch(item.name) === normalizedCandidate)
    ?? packages.find((item) => normalizeForMatch(item.name).includes(normalizedCandidate) || normalizedCandidate.includes(normalizeForMatch(item.name)))
    ?? null;
}

function buildSuggestedQuoteNotes(analysis: LeadConversationAnalysisDTO | null) {
  if (!analysis) return "";
  const lines = [
    analysis.requestedServices.length > 0 ? `Requested services: ${analysis.requestedServices.join(", ")}` : "",
    analysis.serviceAddress ? `Service address: ${analysis.serviceAddress}` : "",
    analysis.scheduleNote ? `Timing note: ${analysis.scheduleNote}` : "",
    analysis.notes.length > 0 ? `Conversation notes: ${analysis.notes.join("; ")}` : ""
  ].filter(Boolean);
  return lines.join("\n");
}

function mergeMultilineText(current: string, addition: string) {
  const trimmedCurrent = current.trim();
  const trimmedAddition = addition.trim();
  if (!trimmedAddition) return trimmedCurrent;
  if (!trimmedCurrent) return trimmedAddition;
  if (trimmedCurrent.includes(trimmedAddition)) return trimmedCurrent;
  return `${trimmedCurrent}\n${trimmedAddition}`;
}

function normalizeForMatch(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
}
